import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaGithub, FaInstagram, FaEnvelope, FaArrowUp } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const iconVariants = {
    hidden: { scale: 0 },
    visible: {
      scale: 1,
      transition: { 
        type: 'spring',
        stiffness: 200,
        damping: 10
      }
    },
    hover: { 
      scale: 1.2,
      rotate: 10,
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.9 }
  };

  return (
    <footer className="py-12 bg-gradient-to-b from-slate-900 to-slate-950 relative">
      {/* 波浪裝飾 */}
      <div className="absolute top-0 left-0 w-full overflow-hidden z-10">
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-12"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
            className="fill-slate-800"
          />
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
            className="fill-slate-800"
          />
          <path
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            className="fill-slate-800"
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 relative z-20">
        <motion.div
          variants={footerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="flex flex-col items-center"
        >
          {/* 返回頂部按鈕 */}
          <motion.div 
            variants={itemVariants} 
            className="mb-8"
          >
            <Link
              to="hero"
              spy={true}
              smooth={true}
              duration={800}
              className="cursor-pointer"
            >
              <motion.div
                className="w-12 h-12 rounded-full bg-gradient-to-r from-primary-600 to-primary-700 flex items-center justify-center text-white shadow-lg shadow-primary-600/20"
                whileHover={{ y: -5, boxShadow: '0 20px 25px -5px rgba(14, 165, 233, 0.4)' }}
                whileTap={{ y: 0, scale: 0.95 }}
              >
                <FaArrowUp />
              </motion.div>
            </Link>
          </motion.div>

          {/* Logo 和社群連結 */}
          <motion.div 
            variants={itemVariants} 
            className="flex flex-col sm:flex-row justify-between items-center w-full mb-8"
          >
            <Link
              to="hero"
              spy={true}
              smooth={true}
              duration={800}
              className="cursor-pointer"
            >
              <motion.div 
                className="text-2xl font-bold text-gradient mb-6 sm:mb-0"
                whileHover={{ scale: 1.05 }}
              >
                胡晉愷
              </motion.div>
            </Link>
            
            <div className="flex space-x-4">
              <motion.a
                href="https://github.com/kaikaichumi"
                target="_blank"
                rel="noopener noreferrer"
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors"
              >
                <FaGithub />
              </motion.a>
              
              <motion.a
                href="https://www.instagram.com/dorapen_photo"
                target="_blank"
                rel="noopener noreferrer"
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors"
              >
                <FaInstagram />
              </motion.a>
              
              <motion.a
                href="mailto:contact@kaikaichumi.com"
                variants={iconVariants}
                whileHover="hover"
                whileTap="tap"
                className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white hover:bg-slate-700 transition-colors"
              >
                <FaEnvelope />
              </motion.a>
            </div>
          </motion.div>

          {/* 導航連結 */}
          <motion.div 
            variants={itemVariants} 
            className="flex flex-wrap justify-center gap-x-8 gap-y-4 mb-8 text-sm"
          >
            <Link
              to="hero"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              首頁
            </Link>
            <Link
              to="about"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              關於我
            </Link>
            <Link
              to="skills"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              技能
            </Link>
            <Link
              to="projects"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              作品集
            </Link>
            <Link
              to="photography"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              攝影作品
            </Link>
            <Link
              to="contact"
              spy={true}
              smooth={true}
              duration={800}
              className="text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              聯絡我
            </Link>
          </motion.div>

          {/* 分隔線 */}
          <motion.div 
            variants={itemVariants} 
            className="w-full h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent mb-6"
          ></motion.div>

          {/* 版權資訊 */}
          <motion.div variants={itemVariants} className="text-center text-gray-500 text-sm">
            <p>© {currentYear} 胡晉愷. 保留所有權利。</p>
            <p className="mt-2">以 React 和 Framer Motion 打造</p>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
